import type { ProfileLocale } from '../../redux/features/jobFormSlice';
import type { CvCheckWarningId } from './types';

/**
 * Word lists for the tone and privacy rules, per CV language. Finnish entries are stems so one
 * entry covers the inflected forms ("tiimipelaaj" matches tiimipelaaja, tiimipelaajana, ...).
 * Matching is case-insensitive substring on lowercased text.
 *
 * Kept deliberately short: every entry is something Finnish recruiters and career coaches call
 * filler. A rule that fires on ordinary wording trains people to ignore the whole check, so when
 * in doubt an entry stays out. Dismiss rates per rule are tracked (cv_studio_action) to prune it.
 */
export const CLICHES: Record<ProfileLocale, readonly string[]> = {
  en: [
    'team player',
    'hard-working',
    'hardworking',
    'results-driven',
    'results-oriented',
    'detail-oriented',
    'go-getter',
    'self-starter',
    'think outside the box',
    'proven track record',
    'strong work ethic',
    'passionate about',
    'dynamic',
    'synergy',
  ],
  fi: [
    'tiimipelaaj',
    'dynaami',
    'intohimoi',
    'tuloshakui',
    'ratkaisukeskei',
    'positiivinen asenne',
    'stressinsieto',
    'monipuolinen osaaja',
    'synergi',
  ],
  sv: [
    'lagspelare',
    'dynamisk',
    'passionerad',
    'brinner för',
    'resultatinriktad',
    'resultatorienterad',
    'stresstålig',
    'positiv attityd',
    'synergi',
  ],
};

/** Openers that describe a duty instead of what the person achieved. Matched at the start of a description. */
export const DUTY_OPENERS: Record<ProfileLocale, readonly string[]> = {
  en: ['responsible for', 'duties included', 'tasks included', 'my duties', 'my tasks', 'in charge of', 'my responsibilities', 'responsibilities included'],
  fi: ['vastuussa', 'tehtäviini kuul', 'työtehtäviini kuul', 'tehtäviin kuul', 'työtehtäviin kuul', 'vastuualueeni', 'vastuualueisiin', 'vastuualueisiini', 'vastuullani oli'],
  sv: ['ansvarig för', 'ansvarade för', 'mina arbetsuppgifter', 'arbetsuppgifterna', 'i mina uppgifter', 'mina ansvarsområden', 'i arbetet ingick'],
};

/** First-person pronouns. Finnish "olen" is normal in a Finnish profile text, so only the pronouns count. */
export const FIRST_PERSON: Record<ProfileLocale, RegExp> = {
  en: /\b(i|my|me|myself)\b/gi,
  fi: /(^|[^\p{L}])(minä|minun|minua|minulla|mä)(?=$|[^\p{L}])/giu,
  sv: /(^|[^\p{L}])(jag|mig|min|mitt|mina)(?=$|[^\p{L}])/giu,
};

/** Language names that count as a working language employers in Finland ask about, in all three UI languages. */
export const WORK_LANGUAGE_NAMES: readonly string[] = [
  'finnish',
  'swedish',
  'english',
  'suomi',
  'ruotsi',
  'englanti',
  'finska',
  'svenska',
  'engelska',
];

/**
 * Patterns for the privacy banner. The Finnish personal identity code (henkilötunnus) is
 * DDMMYY + century sign + 3 digits + check character; the other patterns are words that put
 * protected characteristics (family situation, religion, health) on the CV, which the Equality
 * Act says an employer may not ask about and which a Finnish CV does not need.
 */
export const WARNING_PATTERNS: ReadonlyArray<{ id: CvCheckWarningId; pattern: RegExp }> = [
  { id: 'personalId', pattern: /\b\d{6}[-+a-fu-y]\d{3}[0-9a-y]\b/i },
  {
    id: 'birthDate',
    pattern: /\b(date of birth|born|syntymäaika|syntynyt|födelsedatum|född)\b/i,
  },
  {
    id: 'family',
    pattern: /\b(married|divorced|pregnant|my children|naimisissa|avioliitossa|avoliitossa|eronnut|raskaana|gift med|skild|gravid)\b/i,
  },
  {
    id: 'religion',
    pattern: /\b(religion|religious|church member|uskonto|uskontokunta|kirkon jäsen|religiös|kyrkomedlem)\b/i,
  },
  {
    id: 'health',
    pattern: /\b(diagnosis|diagnosed|chronic illness|diagnoosi|sairaus|pitkäaikaissairaus|diagnos|kronisk sjukdom)\b/i,
  },
];
